import { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { Check, ChevronDown } from 'lucide-react';

export interface PipelineSelectOption {
  value: string;
  label: string;
}

interface PipelineSelectProps {
  value: string;
  options: PipelineSelectOption[];
  onChange: (value: string) => void;
  /** Classes for the trigger button (the header's shared CONTROL style). */
  className?: string;
  ariaLabel?: string;
}

/**
 * A themed replacement for the native pipeline <select> in the ticket headers.
 * The native option list is drawn by the OS and ignores the theme, so this
 * renders its own rounded menu. Same value/onChange contract as the select.
 */
export function PipelineSelect({
  value,
  options,
  onChange,
  className,
  ariaLabel = 'Pipeline',
}: PipelineSelectProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const current = options.find((o) => o.value === value) ?? options[0];

  // Close on outside click and Escape.
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative flex-none">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={clsx(className, 'cursor-pointer')}
      >
        <span className="max-w-[220px] truncate">{current?.label}</span>
        <ChevronDown
          className={clsx(
            'h-[13px] w-[13px] flex-none text-[var(--co-ink-3)] transition-transform duration-[120ms]',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label={ariaLabel}
          className="absolute left-0 z-30 mt-1.5 max-h-72 min-w-full w-max max-w-[320px] overflow-y-auto rounded-[12px] border border-[var(--co-line)] bg-[var(--co-raised)] p-1 shadow-[var(--co-shadow-pop)]"
        >
          {options.map((opt) => {
            const active = opt.value === current?.value;
            return (
              <li key={opt.value} role="option" aria-selected={active}>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    if (!active) onChange(opt.value);
                  }}
                  className={clsx(
                    'flex w-full items-center justify-between gap-3 rounded-[8px] px-3 py-2 text-left text-[13px] transition-colors duration-[120ms]',
                    active
                      ? 'bg-[var(--co-brass-wash)] font-semibold text-[var(--co-brass-ink)]'
                      : 'text-[var(--co-ink-2)] hover:bg-[var(--co-canvas)]',
                  )}
                >
                  <span className="truncate">{opt.label}</span>
                  {active && <Check className="h-3.5 w-3.5 flex-none" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default PipelineSelect;
